import { useEffect, useRef } from "react";

import { useMediaRecorder } from "@/components/webcam-capture";
import { useMultipartUpload } from "@/hooks/use-multipart-upload";

const MAX_FILE_SIZE = 200 * 1024 * 1024;

/**
 * Records the preparation phase and uploads it to S3 as a separate media object.
 * Runs a second MediaRecorder on the same stream as the response recorder
 * — no stream renegotiation needed.
 *
 * Students never see the upload status. `onUploaded` records
 * the media against the turn; `onUploadFailed` reports a failed upload.
 *
 * Records once per turn. It does not follow a mid-preparation stream swap, so
 * the clip truncates there. Only a camera toggle swaps the stream, and that is
 * available only when `vivaCameraRequiredDisabled` is set.
 */
export function usePreparationRecording({
  stream,
  preparationEndDatePassed,
  maxDurationSeconds,
  turnId,
  onUploaded,
  onUploadFailed,
  onIsRecordingChange,
}: {
  stream: MediaStream | null;
  preparationEndDatePassed: boolean;
  maxDurationSeconds: number;
  turnId: string | undefined;
  onUploaded?: (
    mediaId: string,
    mediaUrl: string,
    durationSeconds: number
  ) => void;
  onUploadFailed?: (error: unknown) => void;
  /** Fires when this recorder's own `isRecording` flips — lets callers wait
   * for the shared track to actually be released before starting another
   * recorder on it. */
  onIsRecordingChange?: (isRecording: boolean) => void;
}) {
  const upload = useMultipartUpload();

  // Tracks the turn we've already started recording for keying on turnId
  const startedForTurnRef = useRef<string | null>(null);
  const turnIdRef = useRef(turnId);
  const onUploadedRef = useRef(onUploaded);
  const onUploadFailedRef = useRef(onUploadFailed);
  const uploadRef = useRef(upload);

  useEffect(() => {
    turnIdRef.current = turnId;
    onUploadedRef.current = onUploaded;
    onUploadFailedRef.current = onUploadFailed;
    uploadRef.current = upload;
  });

  const kind =
    stream != null && stream.getVideoTracks().length > 0 ? "video" : "audio";

  const { startRecording, stopRecording, isRecording } = useMediaRecorder({
    stream,
    kind,
    bitrate: 1000000,
    maxFileSize: MAX_FILE_SIZE,
    maxDurationSeconds,
    onMediaCaptured(media, durationSeconds) {
      // The preparation capture is upload-only — we never render a preview from
      // the blob URL aphrodite side, so revoke it immediately to avoid leaking it.
      URL.revokeObjectURL(media.url);
      if (turnIdRef.current == null) return;
      void uploadRef.current
        .start(media.file)
        .then((uploaded) => {
          onUploadedRef.current?.(uploaded.id, uploaded.url, durationSeconds);
        })
        .catch((e) => {
          onUploadFailedRef.current?.(e);
        });
    },
  });

  // Start recording once per turn when the stream becomes available. When the
  // turn changes (reattempt), the guard falls through and recording restarts
  // for the new turn, so correctness no longer relies on a remount.
  useEffect(() => {
    if (
      stream == null ||
      turnId == null ||
      preparationEndDatePassed ||
      startedForTurnRef.current === turnId
    ) {
      return;
    }
    startedForTurnRef.current = turnId;
    startRecording();
  }, [stream, preparationEndDatePassed, turnId, startRecording]);

  // Stop when preparation ends.
  useEffect(() => {
    if (preparationEndDatePassed && isRecording) {
      stopRecording();
    }
  }, [preparationEndDatePassed, isRecording, stopRecording]);

  // Fires only once onstop has actually run, not merely once stopRecording()
  // was called.
  useEffect(() => {
    onIsRecordingChange?.(isRecording);
  }, [isRecording, onIsRecordingChange]);
}
